import useAuthStore from '../auth.store.js';
import { getCurrentUser, clearCurrentUser } from '../services/user.service.js';
import { signUpCognito, signInCognito, confirmSignUpCognito, resendSignUpCodeCognito, confirmResetPasswordCognito, signOutCognito, resetPasswordCognito, signInWithRedirectCognito, changePassword as changePassword$1, confirmSignInWithCodeCognito } from '../cognito/cognito.helper.js';
import AuthError from '../auth.errors.js';
import { getAmplifyAuthFlowType } from '../config.js';
import { fetchAuthDetails } from './auth-action.helpers.js';

const setLoginState = (isLoggedIn)=>{
    useAuthStore.setState({
        isLoggedIn
    });
    if (!isLoggedIn) {
        clearCurrentUser();
    }
};
let isOAuthCompleting = false;
const handleOAuthCompletion = async ()=>{
    // Prevent concurrent OAuth completion attempts
    if (isOAuthCompleting) {
        return null;
    }
    try {
        isOAuthCompleting = true;
        // Use the service layer that properly sets user in Zustand
        const currentUser = await getCurrentUser(true) // Force refresh after OAuth
        ;
        if (currentUser) {
            setLoginState(true);
            return currentUser;
        } else {
            setLoginState(false);
            return null;
        }
    } catch (error) {
        console.error('OAuth completion failed:', error);
        setLoginState(false);
        return null;
    } finally{
        // Reset flag immediately after async operation completes
        isOAuthCompleting = false;
    }
};
const initializeAuth = async ()=>{
    useAuthStore.setState({
        isInitializing: true
    });
    try {
        const authDetails = await fetchAuthDetails({
            forceRefresh: true
        });
        setLoginState(authDetails.isLoggedIn);
    } catch (error) {
        setLoginState(false);
    } finally{
        useAuthStore.setState({
            isInitializing: false
        });
    }
};
const signUp = async (email, password, firstName, lastName, clientMetadata, userAttributes)=>{
    try {
        useAuthStore.setState({
            isInitializing: true
        });
        const signUpResponse = await signUpCognito({
            username: email?.toLowerCase().trim(),
            password,
            options: {
                clientMetadata: {
                    email_verification_required: 'true',
                    ...clientMetadata ? clientMetadata : {}
                },
                userAttributes: {
                    name: firstName,
                    family_name: lastName,
                    email: email,
                    ...userAttributes ? userAttributes : {}
                }
            }
        });
        return signUpResponse;
    } catch (error) {
        throw AuthError.constructError(error).notifyError({
            from: 'signUp action'
        });
    } finally{
        useAuthStore.setState({
            isInitializing: false
        });
    }
};
const confirmSignUp = async (email, confirmationCode)=>{
    try {
        useAuthStore.setState({
            isInitializing: true
        });
        const response = await confirmSignUpCognito({
            username: email?.toLowerCase().trim(),
            confirmationCode
        });
        return response;
    } catch (error) {
        throw AuthError.constructError(error).notifyError({
            from: 'confirmSignUp action'
        });
    } finally{
        useAuthStore.setState({
            isInitializing: false
        });
    }
};
const resendSignUpCode = async (email, metadata)=>{
    try {
        useAuthStore.setState({
            isInitializing: true
        });
        const response = await resendSignUpCodeCognito({
            username: email?.toLowerCase().trim(),
            options: {
                clientMetadata: {
                    ...metadata ? metadata : {}
                }
            }
        });
        return response;
    } catch (error) {
        throw AuthError.constructError(error).notifyError({
            from: 'resendSignUpCode action'
        });
    } finally{
        useAuthStore.setState({
            isInitializing: false
        });
    }
};
const signIn = async (email, password, metadata)=>{
    try {
        useAuthStore.setState({
            isInitializing: true
        });
        let signInResponse = await signInCognito({
            username: email?.toLowerCase().trim(),
            password: password,
            options: {
                authFlowType: getAmplifyAuthFlowType(),
                clientMetadata: metadata
            }
        });
        if (!signInResponse.isSignedIn && 'resetPasswordRequired' in signInResponse && signInResponse.resetPasswordRequired) {
            return {
                isSignedIn: false,
                resetPasswordRequired: true
            };
        }
        if (!signInResponse.isSignedIn) {
            return signInResponse;
        }
        try {
            const currentUser = await getCurrentUser();
            if (currentUser) {
                setLoginState(true);
            } else {
                console.warn('Sign-in succeeded but getCurrentUser returned null');
                setLoginState(false);
            }
        } catch (error) {
            console.error('Sign-in succeeded but getCurrentUser failed:', error);
            setLoginState(false);
        }
        return {
            isSignedIn: true
        };
    } catch (error) {
        const authError = AuthError.constructError(error);
        if (authError.name === 'UserNotFoundException') {
            throw authError;
        }
        throw authError.notifyError({
            from: 'signIn action'
        });
    } finally{
        useAuthStore.setState({
            isInitializing: false
        });
    }
};
const resetPassword = async (email, metadata)=>{
    return await resetPasswordCognito({
        username: email.toLowerCase(),
        options: {
            clientMetadata: {
                ...metadata ? metadata : {}
            }
        }
    });
};
const confirmResetPassword = async (email, code, newPassword, options)=>{
    try {
        useAuthStore.setState({
            isInitializing: true
        });
        const response = await confirmResetPasswordCognito({
            confirmationCode: code,
            newPassword: newPassword,
            username: email.trim().toLowerCase()
        });
        if (options?.globalSignOut) {
            // First sign in to get valid tokens
            const signInResponse = await signInCognito({
                username: email?.toLowerCase().trim(),
                password: newPassword,
                options: {
                    authFlowType: getAmplifyAuthFlowType()
                }
            });
            if (signInResponse.isSignedIn) {
                // Clear auth state before global sign-out to prevent token usage
                setLoginState(false);
                clearCurrentUser();
                // Perform global sign-out
                await signOutCognito({
                    global: true
                });
            }
        } else {}
        return response;
    } catch (error) {
        throw AuthError.constructError(error).notifyError({
            from: 'confirmResetPassword action'
        });
    } finally{
        useAuthStore.setState({
            isInitializing: false
        });
    }
};
const changePassword = async (oldPassword, newPassword)=>{
    try {
        useAuthStore.setState({
            isInitializing: true
        });
        const response = await changePassword$1(oldPassword, newPassword);
        return response;
    } catch (error) {
        throw AuthError.constructError(error).notifyError({
            from: 'changePassword action'
        });
    } finally{
        useAuthStore.setState({
            isInitializing: false
        });
    }
};
const changePasswordWithGlobalSignOut = async (oldPassword, newPassword)=>{
    try {
        useAuthStore.setState({
            isInitializing: true
        });
        const currentUser = await getCurrentUser();
        if (!currentUser?.username) {
            throw new Error('No authenticated user found');
        }
        await changePassword$1(oldPassword, newPassword);
        // Clear auth state before global sign-out to prevent token usage
        setLoginState(false);
        clearCurrentUser();
        // Perform global sign-out
        await signOutCognito({
            global: true
        });
        // Sign in again with fresh tokens
        const signInResponse = await signInCognito({
            username: currentUser.username,
            password: newPassword,
            options: {
                authFlowType: getAmplifyAuthFlowType()
            }
        });
        if (signInResponse.isSignedIn) {
            setLoginState(true);
        }
        return {
            success: true
        };
    } catch (error) {
        throw AuthError.constructError(error).notifyError({
            from: 'changePasswordWithGlobalSignOut action'
        });
    } finally{
        useAuthStore.setState({
            isInitializing: false
        });
    }
};
const signInWithRedirect = async (provider)=>{
    try {
        await signInWithRedirectCognito({
            provider
        });
    } finally{
        useAuthStore.setState({
            isInitializing: false
        });
    }
};
/**
 * Sign in with code (OTP) - initiates custom auth flow
 * Simplified approach: call signIn without password to trigger OTP email
 * Usage: const user = await signInWithCode('user@example.com');
 */ const signInWithCode = async (email, metadata)=>{
    try {
        useAuthStore.setState({
            isInitializing: true
        });
        const response = await signInCognito({
            username: email?.toLowerCase().trim(),
            options: {
                authFlowType: 'CUSTOM_WITHOUT_SRP',
                clientMetadata: {
                    ...metadata ? metadata : {}
                }
            }
        });
        if ('needCustomChallenge' in response && response.needCustomChallenge) {
            return {
                nextStep: response.nextStep,
                needCustomChallenge: true
            };
        }
        return response;
    } catch (error) {
        throw AuthError.constructError(error).notifyError({
            from: 'signInWithCode action'
        });
    } finally{
        useAuthStore.setState({
            isInitializing: false
        });
    }
};
/**
 * Send custom challenge answer (OTP code) - completes custom auth flow
 * Simplified approach: send OTP code to complete authentication
 * Usage: const loggedIn = await sendCustomChallengeAnswer('123456');
 */ const sendCustomChallengeAnswer = async (challengeResponse)=>{
    try {
        useAuthStore.setState({
            isInitializing: true
        });
        const response = await confirmSignInWithCodeCognito({
            challengeResponse
        });
        if (response.isSignedIn) {
            try {
                const currentUser = await getCurrentUser();
                if (currentUser) {
                    setLoginState(true);
                } else {
                    console.warn('Sign-in succeeded but getCurrentUser returned null');
                    setLoginState(false);
                }
            } catch (error) {
                console.error('Sign-in succeeded but getCurrentUser failed:', error);
                setLoginState(false);
            }
        }
        return response;
    } catch (error) {
        throw AuthError.constructError(error).notifyError({
            from: 'sendCustomChallengeAnswer action'
        });
    } finally{
        useAuthStore.setState({
            isInitializing: false
        });
    }
};
/**
 * Resend code (OTP) for custom auth flow
 * This triggers a resend of the verification code
 */ const resendCode = async (email, metadata)=>{
    try {
        useAuthStore.setState({
            isInitializing: true
        });
        const response = await signInCognito({
            username: email?.toLowerCase().trim(),
            options: {
                authFlowType: 'CUSTOM_WITHOUT_SRP',
                clientMetadata: {
                    action: 'resendOtp',
                    ...metadata ? metadata : {}
                }
            }
        });
        return response;
    } catch (error) {
        throw AuthError.constructError(error).notifyError({
            from: 'resendCode action'
        });
    } finally{
        useAuthStore.setState({
            isInitializing: false
        });
    }
};

export { changePassword, changePasswordWithGlobalSignOut, confirmResetPassword, confirmSignUp, handleOAuthCompletion, initializeAuth, resendCode, resendSignUpCode, resetPassword, sendCustomChallengeAnswer, signIn, signInWithCode, signInWithRedirect, signUp };
//# sourceMappingURL=auth.actions.js.map
